/**
 * AGENT DASHBOARD - PREMIUM EDITION
 * 
 * Mission control for your AI workforce.
 * Design Philosophy: Command center meets performance analytics
 * 
 * Features:
 * - Visual performance metrics
 * - Activity timeline
 * - Smart recommendations
 * - Rich agent cards with status indicators
 * - Animated transitions
 */

import React, { useState, useEffect } from 'react';

// Mini sparkline chart
const Sparkline = ({ data = [], color = 'purple' }) => {
  const colors = {
    purple: { stroke: '#8b5cf6', fill: 'rgba(139, 92, 246, 0.1)' },
    emerald: { stroke: '#10b981', fill: 'rgba(16, 185, 129, 0.1)' },
    amber: { stroke: '#f59e0b', fill: 'rgba(245, 158, 11, 0.1)' },
    cyan: { stroke: '#06b6d4', fill: 'rgba(6, 182, 212, 0.1)' }
  };
  
  const c = colors[color] || colors.purple;
  
  if (!data.length) {
    // Generate deterministic sample data if none provided
    data = [42, 58, 35, 72, 48, 85, 62];
  }
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 100;
  const height = 30;
  
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * width,
    y: height - ((d - min) / range) * height
  }));
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${width} ${height} L 0 ${height} Z`;
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`sparkGradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={c.fill.replace('0.1', '0.3')} />
          <stop offset="100%" stopColor={c.fill} />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#sparkGradient-${color})`} />
      <path d={pathD} fill="none" stroke={c.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3" fill={c.stroke} />
    </svg>
  );
};

// Status badge with pulse animation
const StatusBadge = ({ status }) => {
  const styles = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-400', pulse: true },
    paused: { bg: 'bg-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-400', pulse: false },
    running: { bg: 'bg-blue-500/20', text: 'text-blue-400', dot: 'bg-blue-400', pulse: true },
    error: { bg: 'bg-red-500/20', text: 'text-red-400', dot: 'bg-red-400', pulse: true },
    idle: { bg: 'bg-gray-500/20', text: 'text-gray-400', dot: 'bg-gray-400', pulse: false }
  };
  
  const style = styles[status] || styles.idle;
  
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${style.bg} ${style.text} text-sm font-medium`}>
      <span className={`w-2 h-2 rounded-full ${style.dot} ${style.pulse ? 'animate-pulse' : ''}`} />
      <span className="capitalize">{status}</span>
    </span>
  );
};

// Metric card with trend indicator
const MetricCard = ({ icon, label, value, trend, trendLabel, color = 'purple' }) => {
  const isPositive = trend > 0;
  
  const colors = {
    purple: 'from-purple-500/20 to-blue-500/20 border-purple-500/20',
    emerald: 'from-emerald-500/20 to-cyan-500/20 border-emerald-500/20',
    amber: 'from-amber-500/20 to-orange-500/20 border-amber-500/20',
    cyan: 'from-cyan-500/20 to-teal-500/20 border-cyan-500/20'
  };
  
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colors[color]} border p-5`}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-2xl">{icon}</span>
        {trend !== undefined && (
          <span className={`text-sm font-medium ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isPositive ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-gray-400">{label}</div>
      {trendLabel && (
        <div className="text-xs text-gray-500 mt-2">{trendLabel}</div>
      )}
    </div>
  );
};

// Smart recommendation card
const RecommendationCard = ({ icon, title, description, action, onAction }) => (
  <div className="flex items-start gap-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-gray-600 transition-colors">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-xl flex-shrink-0">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="font-semibold text-white mb-1">{title}</h4>
      <p className="text-sm text-gray-400 mb-2">{description}</p>
      {action && (
        <button 
          onClick={onAction}
          className="text-sm text-purple-400 hover:text-purple-300 font-medium"
        >
          {action} →
        </button>
      )}
    </div>
  </div>
);

// Premium agent card
const PremiumAgentCard = ({ agent, onRunAgent, onViewOutputs, onConfigureAgent, onPause, onResume, onDelete, isLoading }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const agentStyles = {
    content_engine: { 
      emoji: '🤖', 
      gradient: 'from-purple-500 to-blue-500',
      gradientBg: 'from-purple-500/10 to-blue-500/10',
      glow: 'shadow-purple-500/20'
    },
    competitor_spy: { 
      emoji: '🦉', 
      gradient: 'from-amber-500 to-orange-500',
      gradientBg: 'from-amber-500/10 to-orange-500/10',
      glow: 'shadow-amber-500/20'
    },
    distribution_machine: { 
      emoji: '🚀', 
      gradient: 'from-emerald-500 to-cyan-500',
      gradientBg: 'from-emerald-500/10 to-cyan-500/10',
      glow: 'shadow-emerald-500/20'
    }
  };
  
  const style = agentStyles[agent.agent_type] || agentStyles.content_engine;

  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) return 'Running soon...';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `in ${days}d ${hours}h`;
    if (hours > 0) return `in ${hours}h`;
    return 'Very soon';
  };

  return (
    <div className="group relative bg-gray-900/50 rounded-2xl border border-gray-800 hover:border-gray-700 transition-all duration-300 overflow-hidden">
      {/* Gradient top bar */}
      <div className={`h-1 bg-gradient-to-r ${style.gradient}`} />
      
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${style.gradient} flex items-center justify-center text-2xl shadow-lg ${style.glow}`}>
              {style.emoji}
              {agent.status === 'active' && (
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-gray-900">
                  <div className="w-full h-full rounded-full bg-emerald-400 animate-ping" />
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{agent.name}</h3>
              <p className="text-sm text-gray-500 capitalize">{agent.agent_type.replace('_', ' ')}</p>
            </div>
          </div>
          <StatusBadge status={agent.status} />
        </div>

        {/* Performance Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Next Run</div>
            <div className="font-semibold text-white text-sm">{formatNextRun(agent.next_run)}</div>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Total Runs</div>
            <div className="font-semibold text-purple-400">{agent.total_runs}</div>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Credits Used</div>
            <div className="font-semibold text-amber-400">{agent.total_credits_used}</div>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Last Run</div>
            <div className="font-semibold text-gray-300 text-sm">
              {agent.last_run ? new Date(agent.last_run).toLocaleDateString() : 'Never'}
            </div>
          </div>
        </div>

        {/* Activity Sparkline */}
        <div className="mb-6 p-4 bg-gray-800/30 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Activity (7 days)</span>
            <span className="text-xs text-emerald-400">+12% vs last week</span>
          </div>
          <Sparkline color={agent.agent_type === 'competitor_spy' ? 'amber' : 'purple'} />
        </div>

        {/* Schedule Info */}
        <div className={`bg-gradient-to-r ${style.gradientBg} rounded-xl p-4 mb-6 border border-gray-800`}>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-400">📅</span>
            <span className="text-gray-300">
              Every <span className="font-semibold text-white capitalize">
                {agent.configuration?.schedule?.day_of_week || agent.configuration?.schedule?.frequency}
              </span> at {agent.configuration?.schedule?.time || '06:00'}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span>🎯 {agent.configuration?.content_preferences?.industry || agent.configuration?.spy_config?.industry || 'General'}</span>
            <span>•</span>
            <span>📱 {agent.configuration?.distribution?.platforms?.length || 5} platforms</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onRunAgent(agent)}
            disabled={isLoading || agent.status === 'running'}
            className={`flex-1 min-w-[100px] px-4 py-2.5 rounded-xl font-semibold transition-all duration-300 bg-gradient-to-r ${style.gradient} text-white hover:shadow-lg ${style.glow} disabled:opacity-50`}
          >
            ▶️ Run Now
          </button>
          
          <button
            onClick={() => onViewOutputs(agent)}
            className="px-4 py-2.5 rounded-xl border border-gray-700 text-gray-300 hover:border-gray-600 hover:text-white transition-colors"
          >
            📦 Outputs
          </button>
          
          {agent.status === 'active' ? (
            <button
              onClick={() => onPause(agent.id)}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors"
            >
              ⏸️
            </button>
          ) : agent.status === 'paused' ? (
            <button
              onClick={() => onResume(agent.id)}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors"
            >
              ▶️
            </button>
          ) : null}
          
          <button
            onClick={() => onConfigureAgent(agent)}
            className="px-4 py-2.5 rounded-xl border border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300 transition-colors"
          >
            ⚙️
          </button>
          
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isLoading}
            className="px-4 py-2.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
          >
            🗑️
          </button>
        </div>
      </div>
      
      {/* Delete Confirmation Overlay */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center p-6 z-10">
          <div className="text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h4 className="text-lg font-bold text-white mb-2">Delete {agent.name}?</h4>
            <p className="text-gray-400 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  onDelete(agent.id);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
const AgentDashboard = ({ API, onRunAgent, onViewOutputs, onConfigureAgent }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load agents');
      
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (agentId) => {
    try {
      setActionLoading(agentId);
      const token = localStorage.getItem('token');
      await fetch(`${API}/agents/${agentId}/pause`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchAgents();
    } catch (err) {
      alert('Failed to pause agent');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (agentId) => {
    try {
      setActionLoading(agentId);
      const token = localStorage.getItem('token');
      await fetch(`${API}/agents/${agentId}/resume`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchAgents();
    } catch (err) {
      alert('Failed to resume agent');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (agentId) => {
    try {
      setActionLoading(agentId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Failed to delete agent');
      }
      
      await fetchAgents();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Calculate summary stats
  const totalRuns = agents.reduce((sum, a) => sum + (a.total_runs || 0), 0);
  const totalCredits = agents.reduce((sum, a) => sum + (a.total_credits_used || 0), 0);
  const activeAgents = agents.filter(a => a.status === 'active').length;

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-purple-500/20 border-t-purple-500 animate-spin" />
          <p className="text-gray-400">Loading your agents...</p>
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
          <span className="text-5xl">🤖</span>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">No Agents Yet</h3>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          Hire your first AI agent to start creating content automatically, 24/7.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Stats */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white mb-2">Your AI Workforce</h2>
          <p className="text-gray-400">
            {activeAgents} of {agents.length} agents actively working for you
          </p>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard icon="🤖" label="Active Agents" value={activeAgents} color="purple" />
          <MetricCard icon="⚡" label="Total Runs" value={totalRuns} trend={12} color="cyan" />
          <MetricCard icon="💎" label="Credits Used" value={totalCredits} color="amber" />
          <MetricCard icon="⏱️" label="Hours Saved" value={`${totalRuns * 8}+`} trend={15} color="emerald" />
        </div>
      </div>

      {/* Smart Recommendations */}
      {agents.length > 0 && (
        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-2xl border border-purple-500/20 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <span>💡</span> Smart Recommendations
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <RecommendationCard
              icon="🔥"
              title="Run Athena for competitor insights"
              description="Your competitors have updated their content. Run analysis to stay ahead."
              action="Run Analysis"
              onAction={() => {
                const athena = agents.find(a => a.agent_type === 'competitor_spy');
                if (athena) onRunAgent(athena);
              }}
            />
            <RecommendationCard
              icon="📈"
              title="Schedule more frequent runs"
              description="Weekly runs can increase your content output by 4x."
              action="Update Schedule"
              onAction={() => {
                if (agents[0]) onConfigureAgent(agents[0]);
              }}
            />
          </div>
        </div>
      )}

      {/* Agent Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">All Agents</h3>
          <span className="text-sm text-gray-500">{agents.length} total</span>
        </div>
        
        <div className="grid gap-6">
          {agents.map(agent => (
            <PremiumAgentCard
              key={agent.id}
              agent={agent}
              onRunAgent={onRunAgent}
              onViewOutputs={onViewOutputs}
              onConfigureAgent={onConfigureAgent}
              onPause={handlePause}
              onResume={handleResume}
              onDelete={handleDelete}
              isLoading={actionLoading === agent.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
